<template>
  <div class="sub-section">
    <div class="inferred-type mb-4">
      <span class="type-label">Data Type:</span>
      <b-tag
        v-if="inferredDataType"
        type="is-info is-light">
        {{ inferredTypeLabel }}
      </b-tag>
      <span
        v-else-if="hasMeasure"
        class="type-label has-text-grey">
        Select a measure to detect type
      </span>
    </div>

    <template v-if="inferredDataType">
      <b-field
        v-if="showDecimalPlaceInput"
        class="input-field"
        label="Decimal places:">
        <b-numberinput
          v-model="inputs.decimal_places"
          min="0" />
      </b-field>

      <b-field
        v-if="showNumericOptions"
        class="input-field">
        <b-switch
          v-model="inputs.show_commas"
          class="mt-5"
          type="is-info">
          Delineate thousands with commas
        </b-switch>
      </b-field>

      <b-field
        v-if="showNumericOptions"
        class="">
        <b-switch
          v-model="inputs.parenthesize_negatives"
          class="mt-0"
          type="is-info">
          Display negative values in parentheses
        </b-switch>
      </b-field>

      <b-field
        v-if="showTimeOptions"
        class="">
        <b-switch
          v-model="inputs.mm_ss"
          class="mt-0"
          type="is-info">
          Display time values in HH:MM:SS format
        </b-switch>
      </b-field>

      <b-field
        v-if="showTimeOptions"
        class="">
        <b-switch
          v-model="inputs.show_duration_label"
          class="mt-0"
          type="is-info">
          Show duration label (e.g. hr, min, sec)
        </b-switch>
      </b-field>

      <b-field
        v-if="showCurrencyOption"
        class="">
        <b-switch
          v-model="showCurrency"
          class="mt-0"
          type="is-info">
          Show currency symbol ($)
        </b-switch>
      </b-field>
    </template>
  </div>
</template>

<script>
  export default {
    name: 'DataFormatEditor',
    components: {},
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      measure: {
        type: Object,
        default: null,
      },
      typeOverride: {
        type: String,
        default: null,
      },
      errors: {
        type: Array,
        default: () => [],
      },
    },
    data () {
      return {
        dataTypeLabels: {
          label: 'Label',
          money: 'Currency ($)',
          number: 'Number (#)',
          percentage: 'Percent (%)',
          time: 'Time',
          datetime: 'Datetime',
          duration: 'Duration',
        },

        decimalPlaceDefaults: {
          label: undefined,
          money: 2,
          number: 0,
          percentage: 1,
          time: undefined,
          datetime: undefined,
          duration: undefined,
        },
      }
    },
    computed: {
      showCurrency: {
        get () {
          return (
            this.inputs.show_currency ||
            [null, undefined,].includes(this.inputs.show_currency)
          )
        },
        set (showCurrency) {
          this.inputs.show_currency = showCurrency
        },
      },
      inputs: {
        get () {
          return this.value || {}
        },
        set (value) {
          this.$emit('input', value ? value : null)
        },
      },
      hasMeasure () {
        return !!(this.measure && this.measure.key)
      },
      measureDefinition () {
        if (!this.hasMeasure) return null
        const options = this.$store.getters['app/reportsConfig/companyMeasureOptions']
        return options?.[this.measure.key] || null
      },
      inferredDataType () {
        return this.typeOverride || this.measureDefinition?.data_type || this.inputs.data_type || null
      },
      inferredTypeLabel () {
        return this.dataTypeLabels[this.inferredDataType] || this.inferredDataType
      },
      showDecimalPlaceInput () {
        return ['money', 'number', 'percentage', 'duration',].includes(this.inferredDataType)
      },
      showNumericOptions () {
        return ['money', 'number', 'percentage',].includes(this.inferredDataType)
      },
      showTimeOptions () {
        return this.inferredDataType === 'duration'
      },
      showCurrencyOption () {
        return this.inferredDataType === 'money'
      },
    },
    watch: {
      'inputs.mm_ss' (val) {
        if (val) this.$set(this.inputs, 'show_duration_label', false)
      },
      'inputs.show_duration_label' (val) {
        if (val) this.$set(this.inputs, 'mm_ss', false)
      },
      inferredDataType: {
        handler (newType) {
          if (newType && newType !== this.inputs.data_type) {
            this.inputs.data_type = newType
            const defaultDecimals = this.decimalPlaceDefaults[newType]
            if (defaultDecimals !== undefined && !this.inputs.decimal_places) {
              this.$set(this.inputs, 'decimal_places', defaultDecimals)
            }
          }
          if (!('show_duration_label' in this.inputs)) {
            this.$set(this.inputs, 'show_duration_label', false)
          }
        },
        immediate: true,
      },
    },
  }
</script>

<style lang="scss" scoped>
.inferred-type {
  display: flex;
  align-items: center;
  gap: 8px;

  .type-label {
    font-size: 14px;
    font-weight: 500;
    color: #555;
  }
}
</style>
